import React from "react";

import {
  FieldValueDiffDisplayProps,
  ResolvedItem,
  SelectComponentProps,
} from "../../types";
import {
  extractDisplayValue,
  getRelatedDataItemDisplayLabel,
  isDeferredTextField,
  resolveSelectItems,
} from "./utils";
import {
  FileDisplay,
  TagDisplay,
  TextDisplay,
  UserDisplay,
  ValueDiff,
} from "./displayComponents";
import {
  ContentType,
  DataFieldBaseResponse,
  FieldType,
} from "@/redux/datablocks/types";
import TextSidebarDiff from "@/features/datablocks/details/components/history/TextSidebarDiff";

import "../../styles.css";

const FieldValueDiffDisplay = ({
  field,
  oldValue,
  newValue,
  lookup,
}: FieldValueDiffDisplayProps) => {
  const getSelectComponent = (
    field: DataFieldBaseResponse,
  ): React.FC<SelectComponentProps> | null => {
    const SELECT_CONTENT_TYPE_CONFIG: Partial<
      Record<ContentType, React.FC<SelectComponentProps>>
    > = {
      [ContentType.Users]: UserDisplay,
      [ContentType.Files]: FileDisplay,
    };

    if (field.contentType) {
      return SELECT_CONTENT_TYPE_CONFIG[field.contentType] ?? TagDisplay;
    }

    return TagDisplay;
  };

  const shouldShowArrowBetweenOldAndNew = (
    field: DataFieldBaseResponse,
  ): boolean => field.type !== FieldType.SELECT || !field.multiple;

  if (isDeferredTextField(field)) {
    return (
      <TextSidebarDiff
        oldValue={String(oldValue ?? "")}
        newValue={String(newValue ?? "")}
      />
    );
  }

  if (field.type === FieldType.SELECT) {
    const Component = getSelectComponent(field) ?? TagDisplay;
    let oldVal: ResolvedItem[];
    let newVal: ResolvedItem[];

    if (field.multiple) {
      const oldItems = resolveSelectItems(oldValue, field, lookup);
      const newItems = resolveSelectItems(newValue, field, lookup);

      const oldKeys = new Set(
        oldItems.map(
          (item) => `${item.id}:${getRelatedDataItemDisplayLabel(item.item)}`,
        ),
      );
      const newKeys = new Set(
        newItems.map(
          (item) => `${item.id}:${getRelatedDataItemDisplayLabel(item.item)}`,
        ),
      );

      oldVal = oldItems.filter(
        (item) =>
          !newKeys.has(
            `${item.id}:${getRelatedDataItemDisplayLabel(item.item)}`,
          ),
      );
      newVal = newItems.filter(
        (item) =>
          !oldKeys.has(
            `${item.id}:${getRelatedDataItemDisplayLabel(item.item)}`,
          ),
      );
    } else {
      oldVal = resolveSelectItems(oldValue, field, lookup);
      newVal = resolveSelectItems(newValue, field, lookup);
    }

    return (
      <ValueDiff<ResolvedItem>
        oldItems={oldVal}
        newItems={newVal}
        Component={Component}
        showArrow={shouldShowArrowBetweenOldAndNew(field)}
      />
    );
  }

  return (
    <ValueDiff<string>
      oldItems={oldValue ? [extractDisplayValue(oldValue, field)] : null}
      newItems={newValue ? [extractDisplayValue(newValue, field)] : null}
      Component={TextDisplay}
      showArrow={shouldShowArrowBetweenOldAndNew(field)}
    />
  );
};

export default FieldValueDiffDisplay;
